import React from 'react';
import { LinearProgress } from '@mui/material';

const VIDEO_ACCEPT =
  'video/mp4, video/webm, video/quicktime, video/x-msvideo, video/x-matroska';

const inputClassName =
  'block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4';

/**
 * Tabbed video source selector — external URL or direct file upload.
 * Used on video create/edit screens.
 */
export default function VideoSourceSection({
  videoSource,
  onSourceChange,
  url,
  onUrlChange,
  videoFile,
  onVideoFileChange,
  existingUploadPath = null,
  uploadProgress = null,
  urlPlaceholder = 'https://youtube.com/watch?v=...',
}) {
  const existingFilename = existingUploadPath
    ? existingUploadPath.split('/').pop()
    : '';
  const selectedName = videoFile?.name || existingFilename;

  const handleVideoFileInput = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;

    const allowedTypes = [
      'video/mp4',
      'video/webm',
      'video/quicktime',
      'video/x-msvideo',
      'video/x-matroska',
    ];

    if (!allowedTypes.includes(selected.type)) {
      event.target.value = '';
      onVideoFileChange(null, 'Unsupported file type. Use MP4, MOV, AVI, WebM, or MKV.');
      return;
    }

    onVideoFileChange(selected, null);
  };

  return (
    <div className="md:col-span-2 space-y-4">
      <div>
        <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
          Video Source *
        </label>
        <div
          className="inline-flex rounded-xl border border-stone-300 bg-stone-50 p-1"
          role="tablist"
          aria-label="Video source"
        >
          <button
            type="button"
            role="tab"
            aria-selected={videoSource === 'url'}
            onClick={() => onSourceChange('url')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
              videoSource === 'url'
                ? 'bg-white text-stone-900 shadow-sm ring-1 ring-stone-200'
                : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            Video URL
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={videoSource === 'upload'}
            onClick={() => onSourceChange('upload')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
              videoSource === 'upload'
                ? 'bg-white text-stone-900 shadow-sm ring-1 ring-stone-200'
                : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            Upload Video
          </button>
        </div>
      </div>

      {videoSource === 'url' ? (
        <div role="tabpanel">
          <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
            Video URL *
          </label>
          <input
            type="text"
            value={url || ''}
            placeholder={urlPlaceholder}
            className={inputClassName}
            onChange={(e) => onUrlChange(e.target.value)}
          />
          <p className="mt-2 text-xs text-stone-500">
            Enter a public video link (e.g. YouTube). External URLs are not downloadable in the app.
          </p>
        </div>
      ) : (
        <div role="tabpanel">
          <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
            Video File *
          </label>
          <div
            className="min-h-[160px] border-2 border-dashed rounded-xl bg-stone-50 flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-stone-100 hover:border-stone-400 px-4 py-6 text-center"
            onClick={() => document.getElementById('video-source-file-input').click()}
          >
            <input
              id="video-source-file-input"
              type="file"
              accept={VIDEO_ACCEPT}
              onChange={handleVideoFileInput}
              className="hidden"
            />
            <svg
              className="mx-auto h-10 w-10 text-stone-400 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <p className="text-stone-700 font-medium">
              {selectedName ? 'Click to replace the uploaded video' : 'Click to upload a video file'}
            </p>
            <p className="text-stone-400 text-sm mt-1">MP4, MOV, AVI, WebM, MKV</p>
            {selectedName && (
              <p className="mt-3 rounded-full bg-white px-4 py-1.5 text-sm font-medium text-stone-700 shadow-sm ring-1 ring-stone-200">
                {selectedName}
              </p>
            )}
          </div>

          {uploadProgress != null && uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mt-3">
              <LinearProgress variant="determinate" value={uploadProgress} />
              <p className="mt-1 text-xs text-stone-500 text-right">{uploadProgress}%</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function getVideoSourceFromUrl(url) {
  if (!url) return 'url';
  const normalized = String(url).trim();
  if (normalized.startsWith('uploads/') || normalized.startsWith('/uploads/')) {
    return 'upload';
  }
  return 'url';
}
